<script setup lang="ts">
import { computed } from "vue";

import actions from "~/actions";
import Button from "~/components/dumb/Button.vue";
import ChipRemoveButton from "~/components/dumb/ChipRemoveButton.vue";
import DropdownItemWrapper from "~/components/dumb/DropdownItemWrapper.vue";
import PageIcon from "~/components/dumb/PageIcon.vue";
import Template from "~/components/dumb/Template.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { DEFAULT_DARTBOARD_LABEL } from "~/constants/dartboard";
import { DEFAULT_CHIP_COLOR } from "~/constants/style";
import { DartboardIcon, OpenInternalLinkIcon, SlashIcon } from "~/icons";
import { ButtonSize, ButtonStyle, EditorMode, IconSize, Placement, ViewKind } from "~/shared/enums";
import { useAppStore, useDataStore, usePageStore } from "~/stores";
import { getChipColorsHex } from "~/utils/color";
import { getPageDisplayName } from "~/utils/common";

const props = defineProps<{
  selectedDartboardDuid?: string;
  editorMode: EditorMode;
  colorHex?: string;
  selected?: boolean;
  replaceEnabled?: boolean;
  isMenuItem?: boolean;
  taskDuid?: string;
}>();

const emit = defineEmits<{
  remove: [];
  replace: [];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();

const isTrash = computed(() => appStore.currentPage?.kind === ViewKind.TRASH);

const isChipMode = computed(
  () => props.editorMode === EditorMode.CHIP || props.editorMode === EditorMode.CHIP_RECOMMENDATION
);
const isListMode = computed(() => props.editorMode === EditorMode.LIST);
const isBoardMode = computed(() => props.editorMode === EditorMode.BOARD);
const isTaskDetailMode = computed(() => props.editorMode === EditorMode.DETAIL);
const isFormMode = computed(() => props.editorMode === EditorMode.FORM);
const isFormOrAutomationMode = computed(() => isFormMode.value || props.editorMode === EditorMode.AUTOMATION);
const isFilterMode = computed(() => props.editorMode === EditorMode.FILTER);

const page = computed(() =>
  props.selectedDartboardDuid ? dataStore.getDartboardByDuid(props.selectedDartboardDuid) : undefined
);
const isDefault = computed(() => isFormOrAutomationMode.value && !!props.selectedDartboardDuid && !page.value);
const title = computed(() =>
  isDefault.value ? DEFAULT_DARTBOARD_LABEL : getPageDisplayName(page.value, dataStore.getSpaceByDuid)
);

const space = computed(() => (page.value?.spaceDuid ? dataStore.getSpaceByDuid(page.value.spaceDuid) : undefined));
const spaceAbrev = computed(() => space.value?.abrev);

const colorSet = computed(() => getChipColorsHex(props.colorHex ?? DEFAULT_CHIP_COLOR, pageStore.theme));

const isCurrentDartboard = computed(() => appStore.currentPage?.duid === props.selectedDartboardDuid);

const openDartboardWithTask = () => {
  if (!props.taskDuid) {
    return;
  }
  actions.visualization.navigateToTask(props.taskDuid);
};
</script>

<template>
  <component
    :is="isMenuItem && isFilterMode ? DropdownItemWrapper : 'div'"
    v-bind="{ selected, noPadding: selected && !pageStore.isMobile, replaceEnabled }"
    class="flex justify-between"
    :class="(isListMode || isFormOrAutomationMode || isTaskDetailMode || isFilterMode) && 'w-full'"
    @replace="emit('replace')">
    <component :is="isBoardMode ? Tooltip : Template" :text="isBoardMode ? 'Dartboard' : undefined" class="truncate">
      <div
        class="flex w-full items-center gap-1 truncate rounded-sm border text-md select-none"
        :style="isFilterMode && { color: colorSet.fore, backgroundColor: colorSet.back, borderColor: colorSet.border }"
        :class="{
          'h-5 border-oncolor px-1 py-0 text-xs': isBoardMode,
          '@container': isListMode,
          'py-0.5': !isListMode,
          'max-w-xs border-oncolor px-1': isChipMode,
          'pr-7 pl-1.5 text-sm': isFormOrAutomationMode,
          'px-2 py-[3px] text-left': isTaskDetailMode,
          'border-transparent': !isChipMode && !isBoardMode,
          'h-5 w-max max-w-48 !gap-0.5 px-1 text-xs': isFilterMode,
          'hover:bg-lt': isTaskDetailMode,
          'hover:bg-opposite/10': isChipMode,
        }">
        <template v-if="!isDefault && space">
          <PageIcon :page="space" :size="isBoardMode || isFilterMode ? IconSize.S : IconSize.M" />
          <span :class="isListMode && 'hidden @[150px]:flex'">{{ spaceAbrev }}</span>
          <SlashIcon class="-mx-1" :class="[isBoardMode || isFilterMode ? 'icon-xs' : 'icon-sm']" />
        </template>
        <component
          :is="isDefault ? DartboardIcon : PageIcon"
          :page="page"
          class="shrink-0"
          :size="isBoardMode || isFilterMode ? IconSize.S : IconSize.M" />
        <span class="flex-1 truncate">{{ title }}</span>
        <!-- Quick actions-->
        <div
          v-if="!pageStore.isPublicView && !isTrash && isTaskDetailMode && !isCurrentDartboard && taskDuid"
          class="ml-1 flex items-center gap-1">
          <Tooltip text="Open dartboard" :placement="Placement.TOP" :distance="10">
            <Button
              :btn-style="ButtonStyle.SECONDARY"
              :size="ButtonSize.SMALL"
              is-contrast
              :icon="OpenInternalLinkIcon"
              :icon-size="IconSize.S"
              a11y-label="Open dartboard"
              class="!p-0.5 print:hidden"
              @click.stop="openDartboardWithTask" />
          </Tooltip>
        </div>
        <ChipRemoveButton :selected="selected" internal @remove="emit('remove')" />
      </div>
    </component>
  </component>
</template>
